import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { T } from '@flipster/i18n';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

import { LeverageBadge, OrderSideBadge } from '../../misc/badge';
import { tpslManageModalBunja } from './state';

export const PositionInfo = () => {
  const {
    sideAtom,
    leverageAtom,
    marginType,
    positionType,
    liqPriceAtom,
    entryPriceAtom,
    markPriceAtom,
    pricePrecisionAtom,
    tradeQtyPrecisionAtom,
    positionUnitAtom,
    quoteCurrencyAtom,
    positionQtyAtom,
  } = useBunja(tpslManageModalBunja);

  const orderQty = useAtomValue(positionQtyAtom);
  const side = useAtomValue(sideAtom);
  const leverage = useAtomValue(leverageAtom);
  const liqPrice = useAtomValue(liqPriceAtom);
  const entryPrice = useAtomValue(entryPriceAtom);
  const markPrice = useAtomValue(markPriceAtom);
  const pricePrecision = useAtomValue(pricePrecisionAtom);
  const qtyPrecision = useAtomValue(tradeQtyPrecisionAtom);
  const positionUnit = useAtomValue(positionUnitAtom);
  const quoteCurrency = useAtomValue(quoteCurrencyAtom);

  return (
    <div
      className={stack({
        px: 16,
        py: 12,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'border.default.level-1',
        rounded: 12,
        gap: 12,
      })}
    >
      <div className={hstack({ alignItems: 'center', gap: 8 })}>
        <div className={css({ textStyle: 'Text/L/Monospace/Strong' })}>
          <Formatted.Number value={orderQty} precision={qtyPrecision} toFixed suffix={<span>{positionUnit}</span>} />
        </div>
        <div className={hstack({ alignItems: 'center', gap: 4 })}>
          <OrderSideBadge orderSide={side} hideIcon />
          <LeverageBadge leverage={leverage} marginType={positionType === 'open' ? marginType : undefined} />
        </div>
      </div>
      <dl
        className={hstack({
          gap: 12,
          '& > div': { display: 'flex', flexDir: 'column', alignItems: 'flex-start', flex: 1 },
          '& dt': { textStyle: 'Text/M/Regular', color: 'content.default.level-3' },
          '& dd': { textStyle: 'Text/L/Monospace/Strong', color: 'content.default.level-5' },
        })}
      >
        <div>
          <dt>
            <T id='Assets.r250401.Entry_price' />
          </dt>
          <dd>
            <Formatted.Number value={entryPrice} precision={pricePrecision} toFixed />
          </dd>
        </div>
        <div>
          <dt>
            <T id='Assets.r250401.Mark_price' />
          </dt>
          <dd>
            <Formatted.Number value={markPrice} precision={pricePrecision} toFixed />
          </dd>
        </div>
        <div>
          <dt>
            {marginType === 'Cross' ? (
              <Tooltip.Root placement='right-start'>
                <Tooltip.Trigger asChild>
                  <span
                    className={css({
                      borderBottomWidth: 1,
                      borderBottomStyle: 'dashed',
                      borderBottomColor: 'border.default.level-3',
                    })}
                  >
                    <T id='Position.r250401.Est_liquidation_price' />
                  </span>
                </Tooltip.Trigger>
                <Tooltip.Content
                  portal
                  zIndex={token('zIndex.modal')}
                  bottomSheetTitle={<T id='Position.r250401.Est_liquidation_price' />}
                >
                  <T id='Trade.r260506.Estimated_liquidation_price_desc' />
                </Tooltip.Content>
              </Tooltip.Root>
            ) : (
              <T id='Assets.r250401.Liq_price' />
            )}
          </dt>
          <dd>
            <Formatted.Number
              value={liqPrice}
              precision={pricePrecision}
              toFixed
              zeroString='-'
              suffix={quoteCurrency}
            />
          </dd>
        </div>
      </dl>
    </div>
  );
};
